import { clone, findIndex, findWhere, isArray, template } from "underscore";

import { Filter, Layout, Query } from "../types/GlobalInterfaces";
import { Module } from "../types/TenantInterfaces";
import StrUtils from "./StrUtils";

export default class QueryUtils {
  static getEmptyFilter() {
    return {
      Field: "",
      Method: "",
      Value: "",
    };
  }

  static getEmptyFilterGroup() {
    return {
      Group: true,
      Filters: [this.getEmptyFilter()],
    };
  }

  static getStartingQuery() {
    return {
      Query: {
        Fields: ["*"],
        Filters: [this.getEmptyFilterGroup()],
        OrderBy: {},
        Limit: {},
        Pagination: {},
      },
    };
  }

  static prefillFilterVariables(filters: Filter[], variables: any) {
    let out: Filter[] = [];

    if (!filters) {
      return out;
    }

    for (let i = 0; i < filters.length; i++) {
      let f = filters[i];

      if (f.Group) {
        f.Filters = this.prefillFilterVariables(f.Filters || [], variables);
      } else {
        f.Value = StrUtils.templateToReadable(f.Value, variables);
      }

      out.push(f);
    }

    return out;
  }

  static substituteQueryVariables(query: Query, variableValues: any) {
    if (!variableValues) return query;

    const filters = query.Filters;

    if (!filters?.length) return query;

    for (let i = 0; i < filters.length; i++) {
      const filter = filters[i];

      if (filter && filter.Group) {
        if (filter.Filters && filter?.Filters?.length) {
          for (let j = 0; j < filter?.Filters.length; j++) {
            let value = filter.Filters[j].Value;

            try {
              if (isArray(value)) {
                value = clone(value);

                for (let k = 0; k < value.length; k++) {
                  let tmpTemplate = template(value[k], {
                    interpolate: /\{\{(.+?)\}\}/g,
                  });

                  value[k] = tmpTemplate(variableValues);
                }
              } else {
                let valTemplate = template(value, {
                  interpolate: /\{\{(.+?)\}\}/g,
                });

                value = valTemplate(variableValues);
              }

              filter.Filters[j].Value = value;
            } catch (e) {
              console.error(e);
            }
          }
        }
      } else {
        let value = filter.Value;

        try {
          if (isArray(value)) {
            value = clone(value);

            for (let j = 0; j < value.length; j++) {
              let tmpTemplate = template(value[j], {
                interpolate: /\{\{(.+?)\}\}/g,
              });

              value[j] = tmpTemplate(variableValues);
            }
          } else {
            let valTemplate = template(value, {
              interpolate: /\{\{(.+?)\}\}/g,
            });

            value = valTemplate(variableValues);
          }

          filter.Value = value;
        } catch (e) {
          console.error(e);
        }
      }
    }

    return query;
  }

  static appendDefaultFilters(
    filters: Filter[],
    filterIdentifier: string,
    searchedValue: string
  ) {
    filters.push({
      Group: true,
      ...(filterIdentifier && { [filterIdentifier]: true }),
      Filters: [
        {
          Field: "_RecordName",
          Method: "contains",
          Value: searchedValue,
        },
      ],
    });

    filters.push({
      Group: true,
      ...(filterIdentifier && { [filterIdentifier]: true }),
      Filters: [
        {
          Field: "id",
          Method: "equals",
          Value: searchedValue,
        },
      ],
    });
  }

  /**
   * This function will append "Filters" to an existing query and return a new query
   * @return {Object} - Returns complete query with appended filters: {Aggregations: [], Fields: [], Filters: []. Pagination: {}}
   * @param {String} searchedValue - Searched value
   * @param {Object} dataSource - Data source from where records that match the Field searchedValue will be searched
   * @param {Object} existingQuery - Existing query (default: empty query)
   * @param {string} filterIdentifier - Optional string to identify the filters being added (So they specifically can be deleted/updated)
   * @param {boolean} mergeFilters - If true, the filters on the existingQuery will all be merged with the new filters at the end.
   */
  static appendFilterToQuery = ({
    searchedValue,
    dataSource,
    existingQuery = {
      Aggregations: [],
      Fields: [],
      Filters: [],
      Pagination: { Page: 1, Count: 25 },
    },
    filterIdentifier = null,
    mergeFilters = true,
  }: {
    searchedValue: string;
    dataSource: Module;
    existingQuery?: Query;
    filterIdentifier: string | null;
    mergeFilters?: boolean;
  }) => {
    let finalQuery = existingQuery;

    if (!searchedValue) return existingQuery;

    if (!dataSource) {
      console.warn("No data source was provided");
      return;
    }

    let searchValues: string[] = [];

    if (typeof searchedValue === "string" && searchedValue.includes(";")) {
      searchValues = searchedValue.split(";").map((v) => v.trim());
    } else {
      searchValues = [searchedValue];
    }

    let newFilters = [];
    for (let sv of searchValues) {
      if (dataSource.Templates?.QuickSearch?.length > 0) {
        for (let j = 0; j < dataSource?.Templates?.QuickSearch?.length; j++) {
          const quickSearchFieldAPIName = dataSource.Templates.QuickSearch[j];
          if (typeof quickSearchFieldAPIName !== "string") continue;

          const baseFieldAPIName = quickSearchFieldAPIName.split(".")[0];
          const subFieldAPIName = quickSearchFieldAPIName.split(".")[1];

          let quickSearchField = findWhere(dataSource.Fields, {
            APIName: baseFieldAPIName,
          });

          // Support for Subitem fields
          if (subFieldAPIName) {
            quickSearchField = findWhere(
              quickSearchField?.SubitemFields || [],
              {
                APIName: subFieldAPIName,
              }
            );
          }

          if (!quickSearchField || !quickSearchField?.FieldType) continue;

          // Maintain original field API name, even for Subitem fields.
          const fieldAPIName = quickSearchFieldAPIName;
          const fieldType = quickSearchField.FieldType;
          const fieldMethods = StrUtils.getFilterMethodsByFieldType(fieldType);

          if (
            findIndex(fieldMethods, { Value: "contains" }) > -1 ||
            findIndex(fieldMethods, { Value: "equals" }) > -1
          ) {
            if (fieldType === "MultiReference") {
              newFilters.push({
                Group: true,
                ...(filterIdentifier && { [filterIdentifier]: true }),
                Filters: [
                  {
                    Field: fieldAPIName,
                    Method: "contains",
                    Value: [{ id: sv }],
                  },
                ],
              });
              newFilters.push({
                Group: true,
                ...(filterIdentifier && { [filterIdentifier]: true }),
                Filters: [
                  {
                    Field: fieldAPIName + "._RecordName",
                    Method: "contains",
                    Value: sv,
                  },
                ],
              });
              continue;
            }
            if (fieldType === "SingleReference") {
              newFilters.push({
                Group: true,
                ...(filterIdentifier && { [filterIdentifier]: true }),
                Filters: [
                  {
                    Field: fieldAPIName,
                    Method: "equals",
                    Value: { id: sv },
                  },
                ],
              });
              newFilters.push({
                Group: true,
                ...(filterIdentifier && { [filterIdentifier]: true }),
                Filters: [
                  {
                    Field: fieldAPIName + "._RecordName",
                    Method: "contains",
                    Value: sv,
                  },
                ],
              });
              continue;
            }
            if (fieldType === "Tags") {
              newFilters.push({
                Group: true,
                ...(filterIdentifier && { [filterIdentifier]: true }),
                Filters: [
                  {
                    Field: fieldAPIName,
                    Method: "contains",
                    Value: [sv],
                  },
                ],
              });
              continue;
            }
            if (fieldType === "Location") {
              newFilters.push({
                Group: true,
                ...(filterIdentifier && { [filterIdentifier]: true }),
                Filters: [
                  {
                    Field: fieldAPIName,
                    Method: "contains",
                    Value: { Readable: sv },
                  },
                ],
              });
              continue;
            }

            if (fieldType === "Number" || fieldType === "Percent") {
              newFilters.push({
                Group: true,
                ...(filterIdentifier && { [filterIdentifier]: true }),
                Filters: [
                  {
                    Field: fieldAPIName,
                    Method: "equals",
                    Value: sv,
                  },
                ],
              });
            }

            if (fieldType === "Object" || fieldType === "Time") {
              // TODO: Make query for object and time
              continue;
            }

            newFilters.push({
              Group: true,
              ...(filterIdentifier && { [filterIdentifier]: true }),
              Filters: [
                {
                  Field: fieldAPIName,
                  Method: fieldAPIName === "id" ? "equals" : "contains",
                  Value: sv,
                },
              ],
            });
          }
        }
      } else {
        this.appendDefaultFilters(newFilters, filterIdentifier || "", sv);
      }

      if (newFilters.length === 0) {
        this.appendDefaultFilters(newFilters, filterIdentifier || "", sv);
      }
    }

    if (mergeFilters) {
      newFilters = this.mergeFilters(newFilters, finalQuery.Filters);
    }

    finalQuery.Filters = newFilters;

    return finalQuery;
  };

  /**
   * mergeFilters - Helper function that merges 'mergeFilters' into 'baseFilters'
   * @param {object} baseFilters
   * @param {object} mergeFilters
   */
  static mergeFilters = (baseFilters: Filter[], mergeFilters: Filter[]) => {
    // return [...mergeFilters, ...baseFilters];÷
    // If there is no base filters, return mergeFilters
    if (!baseFilters || !baseFilters.length) return mergeFilters || [];

    // If there is no mergeFilters, return baseFilters
    if (!mergeFilters || !mergeFilters.length) return baseFilters || [];

    for (const mFilters of mergeFilters) {
      for (const bFilters of baseFilters) {
        bFilters.Filters = [
          ...(bFilters.Filters || []),
          ...(mFilters.Filters || []),
        ];
      }
    }

    return baseFilters;
  };

  /**
   * @param {object} topLevelFilter  - Top level filter
   * @param {object} query - Query
   * @returns {object} query - Modified query with top level filters appended
   */
  static mergeTopLevelFilter = (
    topLevelFilter: Filter[],
    originalQuery: Query
  ) => {
    const query = originalQuery;

    if (topLevelFilter && (!query?.Filters || query?.Filters?.length === 0)) {
      query.Filters = topLevelFilter;
      return query;
    }

    // Merge workspace filter with every group
    if (topLevelFilter && query?.Filters?.length > 0) {
      const newFilters = [];

      for (const topFilter of topLevelFilter) {
        for (const qFilter of query?.Filters ?? []) {
          if (!qFilter?.Filters?.length) continue;

          const newFilter = {
            Group: true,
            Filters: [...qFilter.Filters, ...(topFilter.Filters || [])],
          };

          newFilters.push(newFilter);
        }
      }

      query.Filters = newFilters;
    }

    return query;
  };
}

// Return true/false on whether or not a given query is valid
// checkForFields - Allows you to disable field checking if you only want to validate filters
export const isValidQuery = (query: Query, checkForFields = true) => {
  if (!query) return false;

  const { Fields, Filters } = query;

  if (checkForFields && (!Fields || Fields.length < 1)) return false;
  if (!Filters || Filters.length < 1) return false;

  for (const group of Filters) {
    if (group?.Filters?.length) {
      for (const filter of group.Filters) {
        if (
          (!filter?.Field || !filter?.Method) &&
          !filter?.Value &&
          !filter?.Min &&
          !filter?.Max
        ) {
          return false;
        }
      }
    } else {
      if (!group?.Field || !group?.Method || !group?.Value) {
        return false;
      }
    }
  }

  return true;
};

export const getPermissionsQuery = (type: string) => {
  return {
    Fields: "*",
    Filters: [
      {
        Group: true,
        Filters: [{ Field: "Type", Method: "equals", Value: type }],
      },
    ],
  };
};

/**
 * Returns whether the existing query and the new query are the same.
 */
export const filterIsSame = (existingQuery: Query, newQuery: Query) => {
  let jsonExisting = JSON.stringify(existingQuery?.Filters || {});
  let jsonNew = JSON.stringify(newQuery?.Filters || {});

  return jsonExisting === jsonNew;
};

// Helper function getting Table group by, if one is set.
export const GetTableGroupBy = (queryObject: Query | Layout) => {
  if (!queryObject?.GroupBy) {
    return {
      GetAllGrouped: false,
      GroupBy: null,
    };
  }

  return {
    GetAllGrouped: true,
    GroupBy: { Field: queryObject.GroupBy },
  };
};
